<script setup>
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useQuery, useIsMutating } from "@tanstack/vue-query";

import { httpClientOmni } from "@/libraries";
import { IconPointFilled /* IconMessage */ } from "@tabler/icons-vue";

import BeatLoader from "vue-spinner/src/BeatLoader.vue";
import { Badge } from "@/components/v4/badge";

import Notification from "./notification/Notification";
import DialpadPopover from "./dialpad/DialpadPopover.vue";
import Search from "./search/Search";
import ProfileDropdown from "./ProfileDropdown";

import { computed } from "vue";
import { cn } from "@/lib/utils";

const route = useRoute();
const store = useStore();

const userApp = computed(() => store.getters["app/userApp"]);
const socketStatus = computed(() => store.getters["agent/socketStatus"]);
const decodedUserId = computed(
  () => store.getters["localStorage"]?.decodedUserId
);

// const mode = useColorMode({
//   attribute: "class", // Target the class attribute
//   modes: {
//     // Define the classes for light and dark modes
//     light: "", // No specific class for light mode (or whatever you prefer)
//     dark: "vf-dark", // Use 'vf-dark' for dark mode
//   },
// });

const queryAgentState = useQuery({
  queryKey: ["QUERY_AGENT_GET_ONLINE"],
  queryFn: async () => {
    const response = await httpClientOmni.post("/agent/get-status", {
      agent_id: decodedUserId.value,
    });

    return response.data.data?.state;
  },
});

const currentProject = computed(() => {
  const appId = route.params.appId;
  if (appId) {
    const data = userApp.value.filter(function (q) {
      return q.hashed_id === appId;
    });
    if (data.length > 0) {
      return data[0].name;
    }
  }
  return "";
});

const stateIndicator = {
  offline: "var(--neutral-400)",
  online: "var(--success-400)",
  break: "var(--warning-400)",
};

const textStateIndicator = {
  offline: "!vf-text-neutral-400",
  online: "!vf-text-success-400",
  break: "!vf-text-warning-400",
};

const statusVariant = {
  Connected: "outline-success",
  Disconnected: "outline-muted",
};

const agentStatus = computed(() => queryAgentState.data?.value);
const isLoading = computed(() => queryAgentState.isLoading.value);
const isMutating = useIsMutating({ mutationKey: ["AGENT_UPDATE_ONLINE"] });
</script>

<template>
  <nav
    id="vf-layout-navbar"
    :class="[
      'vf-fixed vf-top-0 vf-left-0 vf-h-[72px] vf-w-full vf-bg-white',
      'vf-shadow-sm vf-flex vf-items-center vf-justify-between vf-pr-4 vf-pl-[112px] vf-z-[99]',
    ]"
  >
    <div class="vf-flex vf-flex-col vf-items-start vf-justify-center">
      <div class="vf-flex vf-items-center vf-gap-x-2" v-if="currentProject">
        <span class="vf-text-primary-500 vf-text-lg vf-text-left">{{
          currentProject
        }}</span>
        <Badge
          :variant="statusVariant[socketStatus] || 'offline'"
          size="sm"
          class="!vf-text-[10px] vf-rounded-[50px] vf-px-3"
        >
          {{ socketStatus }}
        </Badge>
      </div>
      <BeatLoader v-else color="var(--primary-500)" size="12px" />
    </div>
    <div class="vf-flex vf-items-center vf-justify-between vf-gap-8">
      <BeatLoader
        v-if="isLoading || isMutating"
        color="var(--primary-500)"
        size="12px"
      />
      <span v-else class="vf-flex vf-items-center">
        <IconPointFilled
          :color="stateIndicator[agentStatus || 'offline']"
          size="20"
        />
        <span
          :class="
            cn('vf-capitalize', textStateIndicator[agentStatus || 'offline'])
          "
        >
          {{ agentStatus }}
        </span>
      </span>
      <Search />
      <DialpadPopover />
      <Notification />
      <ProfileDropdown :defaultAgentStatus="agentStatus" />
    </div>
  </nav>
</template>
